import { Phone } from "lucide-react";
import { FaWhatsapp, FaInstagram, FaFacebookF } from "react-icons/fa";
import { NAV_ITEMS, STORES, PHONE_DISPLAY, PHONE_TEL, WHATSAPP_URL } from "@/lib/svenData";
import { useGoTo } from "@/lib/useGoTo";

export const Footer = () => {
  const goTo = useGoTo();
  return (
  <footer data-testid="atelier-footer-section" className="bg-[#0b0b0c] pt-20 pb-10 border-t border-white/10">
    <div className="max-w-7xl mx-auto px-5 sm:px-8">
      <div className="grid lg:grid-cols-12 gap-12 pb-16 border-b border-white/10">
        <div className="lg:col-span-5">
          <div className="flex items-center gap-3">
            <img src="/images/brand/sven-logo.jpg" alt="Sven Suits logo" className="h-12 w-12 object-cover rounded-sm" />
            <div>
              <p className="font-display text-2xl tracking-[0.18em] text-white">SVEN <br /> SUITS</p>
              <p className="font-mono-ov text-[9px] tracking-[0.3em] uppercase text-[#c5a059]">Bespoke Tailoring · Mumbai</p>
            </div>
          </div>
          <p className="mt-6 text-sm font-light leading-relaxed text-white/50 max-w-sm">
            Fully bespoke suits, tuxedos and eveningwear — cut, fitted and finished by hand
            at our Santacruz and Bandra ateliers.
          </p>
          <p className="mt-6 text-sm font-medium leading-relaxed text-white/80 max-w-sm">
            Explore our Men's Ethnic Wear Courtesies at: <br />
            <a href="https://adityabhardwajofficial.com/" target="_blank" rel="noopener noreferrer" className="text-white/70 hover:text-[#dfcca4] transition-colors font-bold underline hover:text-white">
              Aditya Bhardwaj Official
            </a>
          </p>
          <div className="mt-6 flex items-center gap-4">
            <a data-testid="footer-phone-link" href={PHONE_TEL} className="inline-flex items-center gap-2 text-white/70 hover:text-[#dfcca4] transition-colors">
              <Phone size={14} className="text-[#c5a059]" />
              <span className="font-mono-ov text-xs tracking-wider">{PHONE_DISPLAY}</span>
            </a>

            {/* WhatsApp */}
            <a data-testid="footer-whatsapp-link" href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="text-[#25D366] hover:scale-110 transition-transform">
              <FaWhatsapp size={20} />
            </a>

            {/* Instagram with exact brand gradient */}
            <a 
              data-testid="footer-instagram-link" 
              href="https://www.instagram.com/svensuits/" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="hover:scale-110 transition-transform inline-flex items-center justify-center"
            >
              <span className="bg-gradient-to-tr from-[#f09433] via-[#dc2743] to-[#bc1888] bg-clip-text text-transparent inline-block leading-none">
                <FaInstagram size={20} className="text-transparent" style={{ fill: "url(#instagram-gradient)" }} />
              </span>
              <svg width="0" height="0" className="absolute">
                <linearGradient id="instagram-gradient" x1="100%" y1="100%" x2="0%" y2="0%">
                  <stop stopColor="#fdf497" offset="0%" />
                  <stop stopColor="#fdf497" offset="5%" />
                  <stop stopColor="#fd5949" offset="45%" />
                  <stop stopColor="#d6249f" offset="60%" />
                  <stop stopColor="#285AEB" offset="90%" />
                </linearGradient>
              </svg>
            </a>

            {/* Facebook Badge (Official Blue Circle + White 'f') */}
            <a 
              data-testid="footer-facebook-link" 
              href="https://www.facebook.com/svensuits/" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="w-[20px] h-[20px] rounded-full bg-[#1877F2] text-white flex items-center justify-center hover:scale-110 transition-transform"
            >
              <FaFacebookF size={12} className="ml-[1px] mt-[1px]" />
            </a>
          </div>

          <div className="mt-4 flex items-center gap-3" data-testid="footer-social-slot" />
        </div>

        <div className="lg:col-span-3">
          <p className="font-mono-ov text-[10px] tracking-[0.25em] uppercase text-[#c5a059] mb-5">Explore</p>
          <nav className="flex flex-col gap-3">
            {NAV_ITEMS.concat([{ label: "Book a Consultation", type: "anchor", id: "book" }]).map((l) => (
              <button
                key={l.label}
                data-testid={`footer-nav-${(l.id || l.to).replace("/", "")}`}
                onClick={() => goTo(l)}
                className="text-left text-sm font-light text-white/60 hover:text-[#dfcca4] transition-colors w-fit gold-underline"
              >
                {l.label}
              </button>
            ))}
          </nav>
        </div>

        <div className="lg:col-span-4">
          <p className="font-mono-ov text-[10px] tracking-[0.25em] uppercase text-[#c5a059] mb-5">Ateliers</p>
          <div className="space-y-6">
            {STORES.map((s) => (
              <div key={s.id}>
                <p className="font-display text-sm text-white">{s.name}</p>
                <p className="mt-1.5 text-xs font-light leading-relaxed text-white/50">{s.address}</p>
                <p className="mt-1 font-mono-ov text-[9px] tracking-[0.15em] uppercase text-white/40">{s.hours}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="pt-8 flex flex-col sm:flex-row items-center justify-between gap-3">
        <p className="font-mono-ov text-[10px] tracking-[0.18em] uppercase text-white/35">
          © {new Date().getFullYear()} Sven Suits. All rights reserved.
        </p>
        <p className="font-accent italic text-sm text-white/35">BESPOKE · DESIGNER · MTM</p>
      </div>
    </div>
  </footer>
  );
};